import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import React, { useEffect, useMemo, useState } from 'react'

import { Button } from '@components/mantine/button.component'
import { Select } from '@components/mantine/select.component'
import { Modal } from '@components/modal.component'
import { Required } from '@components/required.component'
import { useLoad } from '@hooks/use-load.hook'
import { useAppState } from '@src/app.state'
import { db } from '@src/firebase-app'
import { dbNames } from '@utils/constants'
import {
  getTodayDate,
  getUserFullName,
  uniqueIdentifierFromDate,
} from '@utils/helpers'
import { toastService } from '@utils/toast.service'

type ShareTemplateModalProps = {
  onReject: () => void
  onResolve: () => void
  template: Template
}

type OrgOption = Organization & { name: string }

type OwnerOption = {
  id: string
  firstName?: string
  lastName?: string
  [key: string]: any
}

export function ShareTemplateModal({ onReject, onResolve, template }: ShareTemplateModalProps) {
  const user = useAppState(state => state.user)
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null)
  const [newAuthor, setNewAuthor] = useState<OwnerOption | null>(null)
  const [sharing, setSharing] = useState(false)

  const [orgs, _setOrgs, _orgOpts] = useLoad<OrgOption[]>(
    [],
    () => {
      if (!user) return new Promise<OrgOption[]>(resolve => resolve([]))
      const q = query(collection(db, dbNames.organizations), orderBy('name'))
      return getDocs(q).then(q => q.docs.map(d => ({ ...d.data(), id: d.id }) as OrgOption))
    },
    [user],
  )

  const [owners, _setOwners, _ownersOpts] = useLoad<OwnerOption[]>(
    [],
    () => {
      if (!selectedOrg) return new Promise<OwnerOption[]>(resolve => resolve([]))
      if (selectedOrg.id === 'super') {
        const q = query(
          collection(db, dbNames.users),
          where(`isSuperAdmin`, '==', true),
        )
        getDocs(q).then(q => q.docs.map(d => ({ ...d.data(), id: d.id })))
      }
      const q = query(
        collection(db, dbNames.users),
        where(`orgRoles.${selectedOrg.id}`, '==', 'owner'),
      )
      return getDocs(q).then(q => q.docs.map(d => ({ ...d.data(), id: d.id }) as OwnerOption))
    },
    [selectedOrg],
  )

  useEffect(() => {
    if (!owners.length) return
    if (owners.length === 1) {
      setNewAuthor(owners[0])
    }
  }, [owners])

  // All orgs for lookup (including super)
  const allOrgs = useMemo(
    () => [{ name: 'Super Users', id: 'super' } as OrgOption, ...orgs],
    [orgs],
  )

  // Org select data with unique identifier suffix
  const orgSelectData = useMemo(
    () =>
      allOrgs.map(org => ({
        value: org.id,
        label:
          org.id === 'super'
            ? org.name
            : `${org.name} #${uniqueIdentifierFromDate(org.createdDate)}`,
      })),
    [allOrgs],
  )

  // All owners for lookup
  const allOwners = useMemo(() => {
    if (owners.find(owner => owner.id === template.authorId)) {
      return owners
    }
    return [
      { firstName: 'Original', lastName: 'author', id: template.authorId } as OwnerOption,
      ...owners,
    ]
  }, [owners, template.authorId])

  // Owner select data with formatted names
  const ownerSelectData = useMemo(
    () =>
      allOwners.map(owner => ({
        value: owner.id,
        label: getUserFullName(owner),
      })),
    [allOwners],
  )

  async function handleShare() {
    const { id, ...rest } = template
    const newTemplate = {
      ...rest,
      editorIds: [] as string[],
      authorId: newAuthor!.id,
      orgId: selectedOrg!.id,
      lastEditedById: '',
      lastEditedDate: '',
      lastPublishedDate: '',
      sharedFromTemplateId: id,
      sharedFromOrgId: template.orgId,
      createdDate: getTodayDate().toISO(),
    }

    setSharing(true)
    try {
      await addDoc(collection(db, dbNames.workflowTemplates), newTemplate)
      onResolve()
    } catch (err) {
      setSharing(false)
      toastService.error('Error sharing template')
    }
  }

  function handleOrgSelect(org: OrgOption | undefined) {
    setSelectedOrg(org || null)
    setNewAuthor(null)
  }

  return (
    <Modal onClose={onReject}>
      <Modal.Header>Send a copy</Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label htmlFor="select-group">
            Select group <Required />
          </label>
          <Select
            data={orgSelectData}
            onChange={(orgId: string) => {
              const org = allOrgs.find(o => o.id === orgId)
              handleOrgSelect(org)
            }}
            value={selectedOrg?.id}
            fullWidth
            placeholder="Select group"
          />
        </div>
        <div className="form-group">
          <label htmlFor="assign-author">
            Assign new author <Required />
          </label>
          <Select
            data={ownerSelectData}
            onChange={(ownerId: string) => {
              const owner = allOwners.find(o => o.id === ownerId)
              setNewAuthor(owner || null)
            }}
            value={newAuthor?.id}
            disabled={!selectedOrg}
            fullWidth
            placeholder="Select author"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onReject}>Cancel</Button>
        <Button
          onClick={handleShare}
          variant="primary"
          disabled={!selectedOrg || !newAuthor}
          loading={sharing}
        >
          Send
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
